import { useMemo } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Lock, Activity, AlertTriangle, ShieldAlert, Zap, Radio } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";

// Infrastructure hotspots (lat/lng) — events near these get severity boost
const INFRASTRUCTURE_HOTSPOTS = [
  { lat: 50.45, lng: 30.52, name: "Kyiv" },
  { lat: 48.38, lng: 35.04, name: "Zaporizhzhia NPP" },
  { lat: 31.77, lng: 35.23, name: "Jerusalem" },
  { lat: 32.08, lng: 34.78, name: "Tel Aviv" },
  { lat: 33.89, lng: 35.50, name: "Beirut" },
  { lat: 36.19, lng: 36.16, name: "Aleppo" },
  { lat: 51.51, lng: -0.13, name: "London" },
  { lat: 38.90, lng: -77.04, name: "Washington DC" },
  { lat: 35.68, lng: 139.69, name: "Tokyo" },
  { lat: 25.28, lng: 51.52, name: "Doha" },
  { lat: 26.07, lng: 50.56, name: "Bahrain" },
  { lat: 27.19, lng: 56.27, name: "Strait of Hormuz" },
  { lat: 30.04, lng: 32.58, name: "Suez Canal" },
  { lat: 12.59, lng: 43.15, name: "Bab el-Mandeb" },
];

const haversine = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

type Severity = "CRITICAL" | "HIGH" | "ELEVATED" | "MODERATE";

const classifySignal = (event: any): { severity: Severity; score: number } => {
  let score = 0;

  // Event type scoring
  const typeScores: Record<string, number> = { missile: 40, explosion: 35, air_raid: 30, drone: 25, movement: 10 };
  score += typeScores[event.event_type] || 10;

  // Recency scoring (last 1h = +30, last 6h = +20, last 24h = +10)
  const ageMs = Date.now() - new Date(event.occurred_at).getTime();
  if (ageMs < 3600000) score += 30;
  else if (ageMs < 21600000) score += 20;
  else if (ageMs < 86400000) score += 10;

  // Proximity to infrastructure
  if (event.lat && event.lng) {
    const minDist = Math.min(...INFRASTRUCTURE_HOTSPOTS.map((h) => haversine(event.lat, event.lng, h.lat, h.lng)));
    if (minDist < 50) score += 25;
    else if (minDist < 150) score += 15;
    else if (minDist < 500) score += 5;
  }

  // Confidence boost
  score += Math.min((event.confidence || 50) / 5, 20);

  let severity: Severity = "MODERATE";
  if (score >= 75) severity = "CRITICAL";
  else if (score >= 55) severity = "HIGH";
  else if (score >= 35) severity = "ELEVATED";

  return { severity, score };
};

const SEVERITY_CONFIG: Record<Severity, { color: string; bgClass: string; icon: any }> = {
  CRITICAL: { color: "text-red-400", bgClass: "bg-red-500/15 border-red-500/30", icon: ShieldAlert },
  HIGH: { color: "text-orange-400", bgClass: "bg-orange-500/15 border-orange-500/30", icon: AlertTriangle },
  ELEVATED: { color: "text-yellow-400", bgClass: "bg-yellow-500/15 border-yellow-500/30", icon: Zap },
  MODERATE: { color: "text-blue-400", bgClass: "bg-blue-500/15 border-blue-500/30", icon: Activity },
};

const EVENT_ICONS: Record<string, string> = {
  missile: "🚀", drone: "🛸", air_raid: "🚨", explosion: "💥", movement: "🔄",
};

const SignalChain = () => {
  const { isPremium } = useAuth();
  const { data: events = [] } = useEvents(undefined, isPremium);

  const signals = useMemo(() => {
    return events
      .map((e) => ({ ...e, ...classifySignal(e) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, isPremium ? 100 : 10);
  }, [events, isPremium]);

  const counts = useMemo(() => {
    const c = { CRITICAL: 0, HIGH: 0, ELEVATED: 0, MODERATE: 0 };
    signals.forEach((s) => c[s.severity]++);
    return c;
  }, [signals]);

  return (
    <section id="signal-chain" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Radio className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <h2 className="font-mono text-sm sm:text-lg font-bold uppercase tracking-wider text-foreground">
              Signal Chain
            </h2>
            <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {(["CRITICAL", "HIGH", "ELEVATED", "MODERATE"] as Severity[]).map((s) => (
              <span key={s} className={`font-mono text-[10px] font-bold ${SEVERITY_CONFIG[s].color}`}>
                {s}: {counts[s]}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <ScrollArea className="h-[400px] sm:h-[500px]">
            <div className="divide-y divide-border">
              {signals.map((signal, i) => {
                const cfg = SEVERITY_CONFIG[signal.severity];
                const Icon = cfg.icon;
                const timeAgo = formatDistanceToNowStrict(new Date(signal.occurred_at), { addSuffix: true });
                const emoji = EVENT_ICONS[signal.event_type] || "⚡";

                return (
                  <div
                    key={signal.id}
                    className={`flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 transition-colors hover:bg-accent/50 ${i === 0 && signal.severity === "CRITICAL" ? "bg-red-500/5" : ""}`}
                  >
                    <div className={`mt-0.5 flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-md border ${cfg.bgClass}`}>
                      <Icon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${cfg.color}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 flex-wrap">
                        <Badge variant="outline" className={`text-[8px] sm:text-[9px] font-mono font-bold px-1 sm:px-1.5 py-0 ${cfg.color} border-current`}>
                          {signal.severity}
                        </Badge>
                        <span className="text-[9px] sm:text-[10px] text-muted-foreground font-mono">{timeAgo}</span>
                        {!isPremium && i >= 5 && (
                          <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                        )}
                      </div>
                      <p className={`text-xs sm:text-sm leading-tight ${!isPremium && i >= 5 ? "blur-sm select-none" : "text-foreground"}`}>
                        {emoji} {signal.title}
                      </p>
                      <div className="mt-1 flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] text-muted-foreground font-mono flex-wrap">
                        {signal.country && <span>{signal.country}</span>}
                        {signal.city && <span>• {signal.city}</span>}
                        <span>• {signal.event_type.toUpperCase()}</span>
                        <span className="hidden sm:inline">• CONF {signal.confidence ?? 50}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {signals.length === 0 && (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No signals detected. Monitoring active.
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {!isPremium && (
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <Lock className="mr-1 inline h-3 w-3" />
              Free users see 10 signals with 24h delay. <span className="text-primary font-semibold cursor-pointer">Upgrade to Premium</span> for 100+ real-time signals with 30s refresh.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default SignalChain;
